import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { supabase } from '../../lib/supabase';
import { useTranslation } from 'react-i18next';

export default function ResourceDetail() {
  const { slug } = useParams();
  const { i18n } = useTranslation();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [copied, setCopied] = useState(false);

  const isFr = i18n.language === 'fr';

  useEffect(() => {
    async function fetchArticle() {
      setLoading(true);
      const { data, error } = await supabase
        .from('resources')
        .select('*')
        .eq('slug', slug)
        .eq('is_published', true)
        .single();

      if (error) {
        console.error("Error loading article:", error);
      } else {
        setArticle(data);
      }
      setLoading(false);
    }

    fetchArticle();
  }, [slug]);

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center text-slate-500 font-medium">{isFr ? 'Chargement...' : 'Loading article...'}</div>;
  }

  if (!article) {
    return <Navigate to={isFr ? "/fr/resources" : "/resources"} replace />;
  }

  const displayTitle = (isFr && article.title_fr) ? article.title_fr : article.title;
  const displayContent = (isFr && article.content_fr) ? article.content_fr : article.content;
  const displaySummary = isFr ? (article.summary_fr || article.summary) : article.summary;
  const canonical = article.canonical_url || `https://www.tariffbeater.com${isFr ? '/fr' : ''}/resources/${article.slug}`;
  const ogImage = article.og_image_url || `https://www.tariffbeater.com/api/og?title=${encodeURIComponent(displayTitle)}&category=${encodeURIComponent(article.category || 'Market Intelligence')}`;

  const enUrl = `https://www.tariffbeater.com/resources/${article.slug}`;
  const frUrl = `https://www.tariffbeater.com/fr/resources/${article.slug}`;

  // ⚡ Share Button Handlers with Fallback
  const handleCopyLink = async () => {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(canonical);
      } else {
        const textArea = document.createElement("textarea");
        textArea.value = canonical;
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (err) {
      console.error("Failed to copy link: ", err);
    }
  };

  const shareLinkedIn = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(canonical)}`;
  const shareX = `https://twitter.com/intent/tweet?url=${encodeURIComponent(canonical)}&text=${encodeURIComponent(displayTitle)}`;

  // JSON-LD Structured Data Schema for Google Rich Snippets
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": displayTitle,
    "description": displaySummary,
    "image": ogImage,
    "author": {
      "@type": "Organization",
      "name": article.author || "TariffBeater Research Team",
      "url": "https://www.tariffbeater.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "TariffBeater Inc.",
      "logo": {
        "@type": "ImageObject",
        "url": "https://www.tariffbeater.com/logo.png"
      }
    },
    "datePublished": article.published_at,
    "mainEntityOfPage": canonical
  };

  return (
    <article className="min-h-screen bg-slate-50 py-12 px-4 sm:px-6 lg:px-8">
      <Helmet>
        {/* Standard SEO */}
        <title>{displayTitle} | TariffBeater</title>
        <meta name="description" content={displaySummary} />
        <link rel="canonical" href={canonical} />

        {/* Bilingual Hreflang Tags */}
        <link rel="alternate" hrefLang="en" href={enUrl} />
        <link rel="alternate" hrefLang="fr" href={frUrl} />
        <link rel="alternate" hrefLang="x-default" href={enUrl} />

        {/* OpenGraph / LinkedIn */}
        <meta property="og:type" content="article" />
        <meta property="og:site_name" content="TariffBeater" />
        <meta property="og:url" content={canonical} />
        <meta property="og:title" content={displayTitle} />
        <meta property="og:description" content={displaySummary} />
        <meta property="og:image" content={ogImage} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="article:published_time" content={article.published_at} />
        <meta property="article:author" content={article.author} />

        {/* Twitter / X */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:site" content="@tariffbeater" />
        <meta name="twitter:url" content={canonical} />
        <meta name="twitter:title" content={displayTitle} />
        <meta name="twitter:description" content={displaySummary} />
        <meta name="twitter:image" content={ogImage} />

        {/* Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(articleSchema)}
        </script>
      </Helmet>

      <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-sm border border-slate-200 p-8 md:p-14">
        
        {/* Back Button */}
        <Link to={isFr ? "/fr/resources" : "/resources"} className="inline-flex items-center text-sm font-semibold text-blue-600 hover:text-blue-800 mb-6">
          ← {isFr ? "Retour aux articles" : "Back to Resources"}
        </Link>

        <div className="flex items-center gap-3 text-xs font-bold text-blue-600 uppercase tracking-widest mb-4">
          <span>{article.category || 'Market Intelligence'}</span>
        </div>

        <h1 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight leading-tight mb-6">
          {displayTitle}
        </h1>

        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 text-sm text-slate-500 border-b border-slate-100 pb-8 mb-8">
          <div>
            <span>{isFr ? 'Par' : 'By'} {article.author}</span>
            <span className="mx-2">•</span>
            <span>{new Date(article.published_at).toLocaleDateString(isFr ? 'fr-CA' : 'en-US', { month: 'long', day: 'numeric', year: 'numeric' })}</span>
          </div>

          {/* ⚡ SHARE BUTTONS BAR */}
          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-slate-400 uppercase mr-1">{isFr ? 'Partager :' : 'Share:'}</span>
            
            <a href={shareLinkedIn} target="_blank" rel="noopener noreferrer" className="p-2 bg-slate-100 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors" title="Share on LinkedIn">
              <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M19 3a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h14m-.5 15.5v-5.3a3.26 3.26 0 0 0-3.26-3.26c-.85 0-1.84.52-2.28 1.3v-1.11h-2.79v8.37h2.79v-4.93c0-.77.62-1.4 1.39-1.4a1.4 1.4 0 0 1 1.4 1.4v4.93h2.75M6.88 8.56a1.68 1.68 0 0 0 1.68-1.68c0-.93-.75-1.69-1.68-1.69a1.69 1.69 0 0 0-1.69 1.69c0 .93.76 1.68 1.69 1.68m1.39 9.94v-8.37H5.5v8.37h2.77z"/></svg>
            </a>

            <a href={shareX} target="_blank" rel="noopener noreferrer" className="p-2 bg-slate-100 hover:bg-slate-200 text-slate-800 rounded-lg transition-colors" title="Share on X">
              <svg className="w-4 h-4 fill-current" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
            </a>

            <button onClick={handleCopyLink} className="p-2 bg-slate-100 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors text-xs font-semibold flex items-center gap-1">
              {copied ? (isFr ? 'Copié !' : 'Copied!') : (isFr ? 'Copier le lien' : 'Copy Link')}
            </button>
          </div>
        </div>

        {/* Content Body */}
        <div 
          className="prose prose-slate max-w-none text-slate-700 leading-relaxed space-y-6 text-lg font-light"
          dangerouslySetInnerHTML={{ __html: displayContent }}
        />

        {/* Lead Conversion CTA */}
        <div className="mt-12 p-8 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 rounded-2xl text-white text-center shadow-xl">
          <h3 className="text-2xl font-bold mb-3">{isFr ? 'Calculez les Vrais Coûts Débarqués' : 'Calculate True Landed Costs for Your Next Build'}</h3>
          <p className="text-slate-300 max-w-2xl mx-auto mb-6 text-sm font-light">
            {isFr 
              ? 'Simulez les coûts d\'expédition interprovinciaux, les obligations fiscales et les variables de conformité de l\'ALEC instantanément sur TariffBeater.' 
              : 'Simulate inter-provincial shipping costs, tax obligations, and CFTA compliance variables instantly on TariffBeater.'}
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to={isFr ? "/fr/simulator" : "/simulator"} className="bg-blue-600 hover:bg-blue-500 text-white px-8 py-3 rounded-lg font-semibold transition-colors">
              {isFr ? 'Lancer le Simulateur →' : 'Launch Trade Simulator →'}
            </Link>
            <Link to={isFr ? "/fr/directory" : "/directory"} className="bg-white/10 hover:bg-white/20 text-white px-8 py-3 rounded-lg font-semibold backdrop-blur-sm transition-colors border border-white/20">
              {isFr ? 'Parcourir le Répertoire' : 'Browse Domestic Directory'}
            </Link>
          </div>
        </div>
      </div>
    </article>
  );
}